import {
  Plus,
  ChevronRight,
  LayoutDashboard,
  Brain,
  Database,
  PanelsTopLeft,
  MessageSquare,
  Inbox,
  Blocks,
  Settings,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// (popup menu removed in favor of inline collapsible submenu)
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupAction,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from "../hooks/useTranslation";
import { ETICHETTA, Iniziale, VOCE } from "./sidebar-stile";

// L'assistente aperto non diventa una pillola: la pillola e' della pagina
// scelta sotto. Il suo nome si fa piu' scuro e l'iniziale viola.
const ASSISTENTE =
  "text-[#334155] hover:bg-[#F1F5F9] hover:text-[#020617] [&>svg]:text-[#94A3B8] data-[active=true]:bg-transparent data-[active=true]:font-semibold data-[active=true]:text-[#020617] data-[active=true]:hover:bg-[#F1F5F9]";

export function NavProjects({ projects }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState(() => {
    const active = (projects || []).find((p) => p.isActive);
    return active ? active.name : null;
  });

  // Keep openKey valid when projects change
  useEffect(() => {
    const names = new Set((projects || []).map((p) => p.name));
    // If current openKey no longer exists, or route moved, derive from route
    if (!names.has(openKey)) {
      const matched = (projects || []).find((p) =>
        location.pathname.startsWith(p.url)
      );
      if (matched) {
        setOpenKey(matched.name);
        return;
      }
      const active = (projects || []).find((p) => p.isActive);
      setOpenKey(active ? active.name : null);
    } else {
      // Adjust open key when route changes to another project
      const matched = (projects || []).find((p) =>
        location.pathname.startsWith(p.url)
      );
      if (matched && matched.name !== openKey) {
        setOpenKey(matched.name);
      }
    }
  }, [projects, location.pathname]);

  const handleCreateBot = () => {
    // Navigate to dedicated wizard page
    navigate("/bots/new");
  };

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel className={ETICHETTA}>{t("nav.yourBots", "Your Bots")}</SidebarGroupLabel>
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarGroupAction
            aria-label={t("nav.createNewBot", "Create a new bot")}
            onClick={handleCreateBot}
          >
            <Plus />
          </SidebarGroupAction>
        </TooltipTrigger>
        <TooltipContent side="right">
          {t("nav.createNewBot", "Create a new bot")}
        </TooltipContent>
      </Tooltip>
      <SidebarMenu>
        {projects.map((item) => (
          <Collapsible
            key={item.name}
            asChild
            open={openKey === item.name}
            onOpenChange={(open) => setOpenKey(open ? item.name : null)}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton isActive={item.isActive} tooltip={item.name} className={ASSISTENTE}>
                  <Iniziale nome={item.name} scelto={item.isActive} />
                  <span className="truncate">{item.name}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <SidebarMenuSub>
                  {[
                    {
                      title: "Overview",
                      icon: LayoutDashboard,
                      label: t("nav.overview", "Overview"),
                      to: item.url,
                      isActive: location.pathname === item.url,
                    },
                    {
                      title: "Analytics",
                      icon: Brain,
                      label: t("nav.analytics", "Analytics"),
                      to: `${item.url}/intelligence`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/intelligence`
                      ),
                    },
                    {
                      title: "Sources",
                      icon: Database,
                      label: t("nav.sources", "Sources"),
                      to: `${item.url}/sources`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/sources`
                      ),
                    },
                    {
                      title: "Widget",
                      icon: PanelsTopLeft,
                      label: t("nav.widget", "Widget"),
                      to: `${item.url}/widget`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/widget`
                      ),
                    },
                    {
                      title: "Conversations",
                      icon: MessageSquare,
                      label: t("nav.conversations", "Conversations"),
                      to: `${item.url}/conversations`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/conversations`
                      ),
                    },
                    {
                      title: "Contact form",
                      icon: Inbox,
                      label: t("nav.contactForm", "Contatti"),
                      to: `${item.url}/contatti`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/contatti`
                      ),
                    },
                    {
                      title: "Integrations",
                      icon: Blocks,
                      label: t("nav.integrations", "Apps & Integrazioni"),
                      to: `${item.url}/integrazioni`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/integrazioni`
                      ),
                    },
                    /*{
                      title: "Playground",
                      to: `${item.url}/playground`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/playground`
                      ),
                    },*/
                    {
                      title: "Settings",
                      icon: Settings,
                      label: t("common.settings", "Settings"),
                      to: `${item.url}/settings`,
                      isActive: location.pathname.startsWith(
                        `${item.url}/settings`
                      ),
                    },
                  ].map((sub) => (
                    <SidebarMenuSubItem key={sub.title}>
                      <SidebarMenuSubButton asChild isActive={sub.isActive} className={`h-8 ${VOCE}`}>
                        <Link to={sub.to}>
                          <sub.icon aria-hidden="true" />
                          <span
                            className={sub.isActive ? "font-medium" : undefined}
                          >
                            {sub.label}
                          </span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
        {projects.length === 0 && (
          <SidebarMenuItem>
            <SidebarMenuButton
              className="text-sidebar-foreground/70 cursor-pointer"
              onClick={handleCreateBot}
            >
              <Plus className="text-sidebar-foreground/70" />
              <span>{t("nav.createBot", "Create Bot")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
